import { gql } from 'graphql-request'
import type { CardBlock, CardBlockType, CardContent, CardOverrides, CardPlan, LocalizedText, PaywallContent } from '@/components/PlanCard'
import { SUPPORTED_LANGUAGES } from '@/components/PlanCard'

/**
 * Shared by the Plan cards view and the Plans tab: the admin API for card templates,
 * plan cards and paywall texts, and helpers for building card content in an editor.
 */

export interface PlanCardTemplate {
  id: number
  name: string
  content: CardContent
  isArchived: boolean
  planCount: number
  organisationCount: number
  updatedAt: string
}

const TEMPLATE_FIELDS = `
  id
  name
  content
  isArchived
  planCount
  organisationCount
  updatedAt
`

export const GET_TEMPLATES = gql`
  query getPlanCardTemplates($includeArchived: Boolean) {
    getPlanCardTemplates(includeArchived: $includeArchived) {
      ${TEMPLATE_FIELDS}
    }
  }
`

export const GET_DEFAULT_CARD = gql`
  query getDefaultPlanCard {
    getDefaultPlanCard
  }
`

export const CREATE_TEMPLATE = gql`
  mutation createPlanCardTemplate($name: String!, $content: JSON!) {
    createPlanCardTemplate(name: $name, content: $content) {
      ${TEMPLATE_FIELDS}
    }
  }
`

export const UPDATE_TEMPLATE = gql`
  mutation updatePlanCardTemplate($id: Int!, $name: String, $content: JSON) {
    updatePlanCardTemplate(id: $id, name: $name, content: $content) {
      ${TEMPLATE_FIELDS}
    }
  }
`

export const DUPLICATE_TEMPLATE = gql`
  mutation duplicatePlanCardTemplate($id: Int!) {
    duplicatePlanCardTemplate(id: $id) {
      ${TEMPLATE_FIELDS}
    }
  }
`

export const ARCHIVE_TEMPLATE = gql`
  mutation setPlanCardTemplateArchived($id: Int!, $archived: Boolean!) {
    setPlanCardTemplateArchived(id: $id, archived: $archived) {
      ${TEMPLATE_FIELDS}
    }
  }
`

export const DELETE_TEMPLATE = gql`
  mutation deletePlanCardTemplate($id: Int!) {
    deletePlanCardTemplate(id: $id)
  }
`

export const SET_PLAN_CARD = gql`
  mutation setSubscriptionPlanCard($planId: Int!, $templateId: Int, $overrides: JSON) {
    setSubscriptionPlanCard(planId: $planId, templateId: $templateId, overrides: $overrides) {
      id
      cardTemplateId
      cardTemplateName
      cardOverrides
      card
    }
  }
`

export const GET_PAYWALL_CONTENT = gql`
  query getPaywallContent($organisationId: Int!) {
    getPaywallContent(organisationId: $organisationId)
  }
`

export const SET_PAYWALL_CONTENT = gql`
  mutation setPaywallContent($organisationId: Int!, $content: JSON) {
    setPaywallContent(organisationId: $organisationId, content: $content)
  }
`

export type { CardOverrides, PaywallContent }

/** The server's message, and the field it names (`blocks[2].text.en`), if any. */
export const serverError = (error: any, fallback: string): { message: string; field: string | null } => {
  const first = error?.response?.errors?.[0]
  return { message: first?.message || fallback, field: first?.extensions?.field ?? null }
}

/** Plans shown in previews when no real plan is at hand. */
export const SAMPLE_PLANS: (CardPlan & { label: string })[] = [
  { label: 'Sample · €29 / month', name: 'Pro monthly', priceAmount: 2900, currency: 'eur', interval: 'month', renews: true, includedSeconds: 3600, moduleNames: ['Coach', 'Role play'] },
  { label: 'Sample · €290 / year', name: 'Pro yearly', priceAmount: 29000, currency: 'eur', interval: 'year', renews: true, includedSeconds: 43200, moduleNames: ['Coach', 'Role play'] },
  { label: 'Sample · €79 one quarter, no renewal', name: 'Three-month pass', priceAmount: 7900, currency: 'eur', interval: 'quarter', renews: false, includedSeconds: 10800, moduleNames: ['Coach', 'Role play'] },
]

export const BLOCK_LABELS: Record<CardBlockType, string> = {
  title: 'Title',
  tagline: 'Tagline',
  price: 'Price',
  divider: 'Divider',
  featuresLabel: 'Features label',
  features: 'Features',
  cta: 'Button',
}

/** A block id unique within `blocks`: the type plus a counter. */
export const newBlockId = (type: CardBlockType, blocks: CardBlock[]): string => {
  const taken = new Set(blocks.map((b) => b.id))
  for (let n = 1; ; n++) {
    const id = `${type}${n}`
    if (!taken.has(id)) return id
  }
}

export const newBlock = (type: CardBlockType, blocks: CardBlock[]): CardBlock => {
  const id = newBlockId(type, blocks)
  switch (type) {
    case 'title':
      return { id, type, text: { en: '{{planName}}' } }
    case 'tagline':
      return { id, type, text: { en: '' } }
    case 'featuresLabel':
      return { id, type, text: { en: "What's included", fr: 'Inclus' } }
    case 'price':
      return { id, type }
    case 'divider':
      return { id, type }
    case 'features':
      return { id, type, items: [{ text: { en: '' } }] }
    case 'cta':
      return { id, type, label: { en: 'Subscribe', fr: "S'abonner" }, style: 'primary', arrow: false }
  }
}

/** What a new template starts from: the mockup's card shape. */
export const starterContent = (): CardContent => {
  const blocks: CardBlock[] = []
  for (const type of ['title', 'tagline', 'price', 'divider', 'featuresLabel', 'features', 'cta'] as CardBlockType[]) {
    blocks.push(newBlock(type, blocks))
  }
  return { version: 1, variant: 'default', badge: null, blocks }
}

/** Every translatable string of a card, so coverage can be counted. */
const textsOf = (content: CardContent): LocalizedText[] => {
  const texts: LocalizedText[] = []
  if (content.badge) texts.push(content.badge.text)
  for (const block of content.blocks) {
    if ('text' in block) texts.push(block.text)
    if (block.type === 'price') {
      if (block.periodLabel) texts.push(block.periodLabel)
      if (block.note) texts.push(block.note)
    }
    if (block.type === 'features') block.items.forEach((item) => texts.push(item.text, ...(item.tag ? [item.tag] : [])))
    if (block.type === 'cta') texts.push(block.label)
  }
  return texts
}

/** Filled strings per language, out of all strings, e.g. `{ en: [9, 9], fr: [6, 9] }`. */
export const coverage = (texts: LocalizedText[]): Record<string, [number, number]> =>
  Object.fromEntries(SUPPORTED_LANGUAGES.map((lang) => [lang, [texts.filter((t) => !!t[lang]?.trim()).length, texts.length]]))

export const cardCoverage = (content: CardContent) => coverage(textsOf(content))

export const paywallTexts = (content: PaywallContent): LocalizedText[] => [
  ...[content.title, content.intro, content.footnote].filter((t): t is LocalizedText => !!t),
  ...content.reassurance.flatMap((item) => [item.title, item.text]),
]

/** Drops a translation set whose every language is blank. */
export const blankText = (text: LocalizedText | undefined | null): boolean => !text || Object.values(text).every((v) => !v?.trim())

export const isOverridden = (overrides: CardOverrides | null | undefined, blockId: string, field: string): boolean => !!(overrides?.blocks?.[blockId] as Record<string, unknown> | undefined)?.[field]
